// src/lib/auth-service.ts
"use client";

import {
  signIn,
  signOut,
  signUp,
  confirmSignUp,
  resendSignUpCode,
  getCurrentUser,
  fetchAuthSession,
  updateUserAttribute,
  updatePassword,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { CognitoAuthError, CognitoErrorCode, AuthErrorMessage } from '@/types/auth';

export interface AuthUserProfile {
  id: string;
  email: string;
  name: string;
}

/**
 * Mapeo de códigos de error de Cognito a claves de traducción i18n
 */
const ERROR_CODE_TO_I18N_KEY: Record<CognitoErrorCode, string> = {
  UserNotFoundException: 'auth.errors.userNotFound',
  NotAuthorizedException: 'auth.errors.invalidCredentials',
  UserNotConfirmedException: 'auth.errors.userNotConfirmed',
  PasswordResetRequiredException: 'auth.errors.passwordResetRequired',
  LimitExceededException: 'auth.errors.tooManyAttempts',
  TooManyRequestsException: 'auth.errors.tooManyAttempts',
  InvalidParameterException: 'auth.errors.invalidParameters',
  NetworkError: 'auth.errors.networkError',
  UsernameExistsException: 'register.errors.emailExists',
  InvalidPasswordException: 'register.errors.invalidPassword',
  CodeMismatchException: 'register.errors.codeMismatch',
  ExpiredCodeException: 'register.errors.codeExpired',
  CodeDeliveryFailureException: 'auth.errors.codeDeliveryFailed',
};

/**
 * Mensajes de error por defecto (fallback si i18n no está disponible)
 */
const ERROR_MESSAGES: Record<CognitoErrorCode, AuthErrorMessage> = {
  UserNotFoundException: {
    es: 'No existe una cuenta con este email',
    en: 'No account exists with this email',
  },
  NotAuthorizedException: {
    es: 'Credenciales incorrectas',
    en: 'Incorrect credentials',
  },
  UserNotConfirmedException: {
    es: 'Debes confirmar tu cuenta. Revisa tu email',
    en: 'You must confirm your account. Check your email',
  },
  PasswordResetRequiredException: {
    es: 'Debes restablecer tu contraseña',
    en: 'You must reset your password',
  },
  LimitExceededException: {
    es: 'Demasiados intentos. Intenta en unos minutos',
    en: 'Too many attempts. Try again in a few minutes',
  },
  TooManyRequestsException: {
    es: 'Demasiados intentos. Intenta en unos minutos',
    en: 'Too many attempts. Try again in a few minutes',
  },
  InvalidParameterException: {
    es: 'Parámetros inválidos',
    en: 'Invalid parameters',
  },
  NetworkError: {
    es: 'Sin conexión. Verifica tu internet',
    en: 'No connection. Check your internet',
  },
  UsernameExistsException: {
    es: 'Ya existe una cuenta con este email',
    en: 'An account with this email already exists',
  },
  InvalidPasswordException: {
    es: 'La contraseña no cumple los requisitos',
    en: 'Password does not meet requirements',
  },
  CodeMismatchException: {
    es: 'Código de verificación inválido',
    en: 'Invalid verification code',
  },
  ExpiredCodeException: {
    es: 'El código de verificación ha expirado. Solicita uno nuevo',
    en: 'Verification code has expired. Request a new one',
  },
  CodeDeliveryFailureException: {
    es: 'No se pudo enviar el código. Intenta nuevamente',
    en: 'Failed to send code. Please try again',
  },
};

const DEFAULT_ERROR: AuthErrorMessage = {
  es: 'Error de autenticación. Intenta nuevamente',
  en: 'Authentication error. Please try again',
};

/**
 * Extrae el código de error de una excepción de Cognito/Amplify
 */
function getCognitoErrorCode(error: unknown): CognitoErrorCode | null {
  if (error && typeof error === 'object') {
    const errorObj = error as Record<string, unknown>;

    // Amplify v6 usa 'name' para el código de error
    if (typeof errorObj.name === 'string' && errorObj.name in ERROR_MESSAGES) {
      return errorObj.name as CognitoErrorCode;
    }

    // Cognito API directa usa '__type' para el código de error
    if (typeof errorObj.__type === 'string' && errorObj.__type in ERROR_MESSAGES) {
      return errorObj.__type as CognitoErrorCode;
    }

    // Verificar errores de red
    const message = typeof errorObj.message === 'string' ? errorObj.message.toLowerCase() : '';
    if (message.includes('network') || message.includes('fetch')) {
      return 'NetworkError';
    }
  }
  return null;
}

/**
 * Obtiene el mensaje de error localizado para un error de Cognito
 */
export function getAuthErrorMessage(error: unknown, locale: 'es' | 'en' = 'es'): string {
  const code = getCognitoErrorCode(error);
  if (code) {
    return ERROR_MESSAGES[code][locale];
  }
  return DEFAULT_ERROR[locale];
}

/**
 * Obtiene la clave i18n para un error de Cognito
 */
export function getAuthErrorI18nKey(error: unknown): string {
  const code = getCognitoErrorCode(error);
  if (code && ERROR_CODE_TO_I18N_KEY[code]) {
    return ERROR_CODE_TO_I18N_KEY[code];
  }
  return 'auth.errors.default';
}

/**
 * Verifica si el error es un error de rate limiting
 */
export function isRateLimitError(error: unknown): boolean {
  const code = getCognitoErrorCode(error);
  return code === 'LimitExceededException' || code === 'TooManyRequestsException';
}

/**
 * Verifica si el error es porque la cuenta no está confirmada
 */
export function isUserNotConfirmedError(error: unknown): boolean {
  const code = getCognitoErrorCode(error);
  return code === 'UserNotConfirmedException';
}

/**
 * Verifica si el error es porque el email ya existe
 */
export function isUsernameExistsError(error: unknown): boolean {
  const code = getCognitoErrorCode(error);
  return code === 'UsernameExistsException';
}

/**
 * Verifica si el error es porque el código ha expirado
 */
export function isExpiredCodeError(error: unknown): boolean {
  const code = getCognitoErrorCode(error);
  return code === 'ExpiredCodeException';
}

/**
 * Autentica al usuario con email y contraseña.
 * @throws CognitoAuthError en caso de fallo
 */
export async function login(email: string, password: string): Promise<AuthUserProfile> {
  // NOTA: Se eliminó la verificación de navigator.onLine porque no es confiable
  // y puede dar falsos negativos. Amplify manejará errores de red reales.

  let result;
  try {
    result = await signIn({
      username: email,
      password,
      options: {
        authFlowType: 'USER_PASSWORD_AUTH',
      },
    });
  } catch (err) {
    // FEATURE-042: Re-lanzar el error original de Cognito para que pueda ser detectado
    // Cognito lanza UserNotConfirmedException directamente cuando la cuenta no está confirmada
    throw err;
  }

  if (result.isSignedIn) {
    const user = await getCurrentUser();
    const session = await fetchAuthSession();

    // Extraer claims del ID token
    const idTokenPayload = session.tokens?.idToken?.payload;

    return {
      id: user.userId,
      email: idTokenPayload?.email as string || email,
      name: idTokenPayload?.name as string || idTokenPayload?.email as string || email,
    };
  }

  // FEATURE-042: Detectar cuenta no confirmada via nextStep
  // En algunos casos, Amplify v6 puede retornar nextStep en lugar de lanzar excepción
  if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
    const error = new Error('User not confirmed') as CognitoAuthError;
    error.name = 'UserNotConfirmedException';
    throw error;
  }

  // Si llegamos aquí, el login requiere pasos adicionales (MFA, etc.)
  // Por ahora lanzamos error ya que MFA no está en scope
  const error = new Error('Login requires additional steps') as CognitoAuthError;
  error.name = 'NotAuthorizedException';
  throw error;
}

/**
 * Cierra la sesión del usuario actual.
 * Limpia todos los tokens de Amplify automáticamente.
 */
export async function logout(): Promise<void> {
  await signOut();
}

/**
 * Obtiene el access token actual.
 * Amplify auto-refresca el token si ha expirado (CB-01).
 * @returns El access token o null si no hay sesión
 */
export async function getAccessToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() || null;
  } catch {
    return null;
  }
}

/**
 * Obtiene el identity token (ID token) del usuario autenticado.
 * Amplify auto-refresca el token si ha expirado (CB-01).
 * @returns El ID token o null si no hay sesión
 */
export async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
}

/**
 * Verifica el estado de autenticación actual.
 * @returns El perfil del usuario o null si no está autenticado
 */
export async function checkAuthStatus(): Promise<AuthUserProfile | null> {
  try {
    const user = await getCurrentUser();
    const session = await fetchAuthSession();

    // Verificar que tenemos tokens válidos
    if (!session.tokens?.accessToken) {
      return null;
    }

    const idTokenPayload = session.tokens?.idToken?.payload;

    return {
      id: user.userId,
      email: idTokenPayload?.email as string || '',
      name: idTokenPayload?.name as string || idTokenPayload?.email as string || '',
    };
  } catch {
    return null;
  }
}

/**
 * Callback type para eventos de autenticación
 */
export type AuthEventCallback = (event: 'signedIn' | 'signedOut' | 'tokenRefresh' | 'tokenRefreshFailure') => void;

/**
 * Suscribe a eventos de autenticación de Amplify Hub.
 * Útil para sincronizar estado entre pestañas (CB-02).
 * @returns Función para cancelar la suscripción
 */
export function subscribeToAuthEvents(callback: AuthEventCallback): () => void {
  const unsubscribe = Hub.listen('auth', ({ payload }) => {
    switch (payload.event) {
      case 'signedIn':
        callback('signedIn');
        break;
      case 'signedOut':
        callback('signedOut');
        break;
      case 'tokenRefresh':
        callback('tokenRefresh');
        break;
      case 'tokenRefresh_failure':
        callback('tokenRefreshFailure');
        break;
    }
  });

  return unsubscribe;
}

/**
 * Limpia tokens legacy del sistema anterior.
 * Llamar durante la migración para evitar conflictos (R-02).
 */
export function clearLegacyTokens(): void {
  if (typeof window === 'undefined') return;

  // Limpiar tokens del sistema anterior
  localStorage.removeItem('accessToken');
  localStorage.removeItem('idToken');
  localStorage.removeItem('user');
  sessionStorage.removeItem('refreshToken');
}

/**
 * Resultado del registro de usuario
 */
export interface RegisterResult {
  isConfirmed: boolean;
  userId?: string;
}

/**
 * Registra un nuevo usuario con email, password y nombre.
 * @throws CognitoAuthError en caso de fallo
 */
export async function register(
  email: string,
  password: string,
  name: string
): Promise<RegisterResult> {
  const result = await signUp({
    username: email,
    password,
    options: {
      userAttributes: {
        email,
        name,
      },
    },
  });

  return {
    isConfirmed: result.isSignUpComplete,
    userId: result.userId,
  };
}

/**
 * Confirma el registro de usuario con código de verificación.
 * @throws CognitoAuthError en caso de fallo
 */
export async function confirmRegistration(
  email: string,
  code: string
): Promise<void> {
  await confirmSignUp({
    username: email,
    confirmationCode: code,
  });
}

/**
 * Reenvía el código de verificación al email del usuario.
 * @throws CognitoAuthError en caso de fallo
 */
export async function resendVerificationCode(email: string): Promise<void> {
  await resendSignUpCode({
    username: email,
  });
}

/**
 * Actualiza el nombre del usuario autenticado en Cognito.
 * @throws CognitoAuthError en caso de fallo
 */
export async function updateUserName(name: string): Promise<void> {
  await updateUserAttribute({
    userAttribute: {
      attributeKey: 'name',
      value: name,
    },
  });
}

/**
 * Cambia la contraseña del usuario autenticado.
 * @throws CognitoAuthError en caso de fallo (NotAuthorizedException si la contraseña actual es incorrecta)
 */
export async function changePassword(
  oldPassword: string,
  newPassword: string
): Promise<void> {
  await updatePassword({
    oldPassword,
    newPassword,
  });
}

/**
 * Solicita un código de recuperación de contraseña.
 * Cognito enviará un código de 6 dígitos al email del usuario.
 * @throws CognitoAuthError en caso de fallo (UserNotFoundException, LimitExceededException, CodeDeliveryFailureException)
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await resetPassword({ username: email });
}

/**
 * Confirma el reset de contraseña con el código de verificación.
 * @throws CognitoAuthError en caso de fallo (CodeMismatchException, ExpiredCodeException, InvalidPasswordException)
 */
export async function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  await confirmResetPassword({
    username: email,
    confirmationCode: code,
    newPassword,
  });
}
